import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react'
import { ThemeContext } from '../contexts/ThemeContext';
import { ResultsContext } from '../contexts/ResultsContext';
import { getBaseUrl } from '../utils/apiUtils';
import { getTournamentTypeDisplay } from '../utils/constants';
import Modal from './Modal';
import './TournamentIdPicker.css';
import { useTournamentAppConfiguration } from "../utils/tournamentconfig/TournamentConfiguration"
import QrScanner from 'qr-scanner';

/**
 * TournamentIdPicker component provides a modal interface for selecting tournament IDs
 * 
 * @param {Object} props - Component props
 * @param {string} props.value - Current tournament ID value
 * @param {Function} props.onChange - Callback function when tournament ID changes
 * @param {string} props.placeholder - Placeholder text for the input field
 * @param {string} props.className - Additional CSS class names
 */
function TournamentIdPicker({ value, onChange, placeholder = "Enter tournament number", className = "", showTrigger = true }) {
  const tournamentAppConfiguration = useTournamentAppConfiguration()

  const { isTournamentPickerOpen, setIsTournamentPickerOpen, setTournamentIdOnly, earlyType, results } = useContext(ResultsContext);
  const [tournaments, setTournaments] = useState([]);
  const [filteredTournaments, setFilteredTournaments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const { theme } = useContext(ThemeContext);

  // Get tournament type from results context
  const tournamentType = earlyType;

  // Find tournament type by ID from the tournaments list
  const findTournamentTypeById = useCallback((id) => {
    const tournament = tournaments.find(t => t.tournamentId.toString() === id);
    return tournament ? tournament.type : null;
  }, [tournaments]);

  // Fetch tournaments from the API
  const fetchTournaments = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await fetch(`${getBaseUrl()}${tournamentAppConfiguration.endPoints.tournamentsList}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setTournaments(data.tournaments || []);
        setFilteredTournaments(data.tournaments || []);
      } else {
        console.error('Failed to fetch tournaments:', response.status);
      }
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    } finally {
      setIsLoading(false);
    }
  }, [tournamentAppConfiguration.endPoints.tournamentsList]);

  // Update tournament ID in context when value changes
  useEffect(() => {
    if (value) {
      // Check if we know the tournament type for this ID
      const knownType = findTournamentTypeById(value);
      setTournamentIdOnly(value, knownType);
    }
  }, [value, setTournamentIdOnly, findTournamentTypeById]);

  const hasFetched = useRef(false);

  // Fetch tournaments when modal is opened, but only once
  useEffect(() => {
    // Fetch early so we can intelligently update the inline 4k/HD tags
    if (!hasFetched.current) {
      fetchTournaments()
      // Only fetch once due to mount (helps in test)
      hasFetched.current = true;
    }
  }, [fetchTournaments]);

  useEffect(() => {
    if (isTournamentPickerOpen && tournaments.length === 0) {
      fetchTournaments();
    }
  }, [isTournamentPickerOpen, tournaments.length, fetchTournaments]);

  // Identify the current tournaments for each unique type
  const currentTournamentsByType = useMemo(() => {
    if (tournaments.length === 0) return {};

    const typeMap = {};
    const now = new Date();

    tournaments.forEach(tournament => {
      const type = tournament.type || 'unknown';
      const startDate = tournament.start ? new Date(tournament.start) : null;
      const endDate = tournament.end ? new Date(tournament.end) : null;

      // A tournament is current if today is between its start and end dates
      // If start or end is null, we use more relaxed conditions
      const isCurrent =
        (startDate && endDate && now >= startDate && now <= endDate) || // Both dates available
        (startDate && !endDate && now >= startDate) || // Only start date available
        (!startDate && endDate && now <= endDate) || // Only end date available
        (!startDate && !endDate && false); // No dates available, not current

      if (isCurrent) {
        // If we already have a tournament of this type, keep the one with the closest end date
        if (!typeMap[type] ||
          (endDate && typeMap[type].end && new Date(typeMap[type].end) > endDate)) {
          typeMap[type] = tournament;
        }
      }
    });

    return typeMap;
  }, [tournaments]);

  // Filter tournaments when search query changes
  useEffect(() => {
    if (tournaments.length === 0) return;

    const query = searchQuery.toLowerCase().trim();

    if (!query) {
      setFilteredTournaments(tournaments);
      return;
    }

    const filtered = tournaments.filter(tournament => {
      const tournamentId = tournament.tournamentId.toString().toLowerCase();
      const name = tournament.name ? tournament.name.toLowerCase() : '';
      const gamesMatch = tournament.games ? tournament.games.some(game =>
        game && game.toLowerCase().includes(query)
      ) : false

      return tournamentId.includes(query) || name.includes(query) || gamesMatch
    });

    setFilteredTournaments(filtered);
  }, [searchQuery, tournaments]);

  // Handle tournament selection
  const handleSelectTournament = (tournamentId) => {
    if (onChange) {
      onChange(tournamentId);

      // Check if we know the tournament type for this ID
      const selectedType = findTournamentTypeById(tournamentId);
      if (selectedType) {
        // Update the results context with the known type
        setTournamentIdOnly(tournamentId, selectedType);
      } else {
        // Just update the ID
        setTournamentIdOnly(tournamentId);
      }

      // Update URL parameter for tournament ID immediately
      const url = new URL(window.location);
      if (tournamentId) {
        url.searchParams.set('tournamentId', tournamentId);
      } else {
        url.searchParams.delete('tournamentId');
      }
      // Preserve username parameter if it exists
      const username = url.searchParams.get('username');
      if (username) {
        url.searchParams.set('username', username);
      }
      window.history.replaceState({}, '', url);

      // Trigger search automatically if we're in the context of a search form
      // Use setTimeout to ensure the state update has completed before clicking the search button
      setTimeout(() => {
        const searchForm = document.querySelector('.tournament-search-form');
        if (searchForm) {
          const searchButton = searchForm.querySelector('.search-button');
          if (searchButton) {
            searchButton.click();
          }
        }
      }, 0);
    }
    setIsTournamentPickerOpen(false);
  };

  // Handle direct input of tournament ID
  const handleDirectInput = (inputValue) => {
    if (inputValue.trim() !== '') {
      handleSelectTournament(inputValue);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle key press in search input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      const query = searchQuery.trim();
      if (query !== '') {
        handleSelectTournament(query);
      }
    }
  };

  /**
   * Extract tournament ID from a URL
   * @param {string} urlStr - The URL or string to extract from
   * @returns {string} The extracted ID or the original string
   */
  const extractIdFromUrl = useCallback((urlStr) => {
    if (!urlStr) return '';
    const trimmed = urlStr.trim();
    try {
      // Try parsing as a URL
      const url = new URL(trimmed);
      const pathname = url.pathname;
      // Remove trailing slash if it exists
      const cleanPath = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
      // Get the last component
      const parts = cleanPath.split('/');
      return parts[parts.length - 1];
    } catch (e) {
      // If it's not a valid URL, check if it's a path-like string
      if (trimmed.includes('/')) {
        // Split by ? to ignore parameters
        const withoutParams = trimmed.split('?')[0];
        const cleanPath = withoutParams.endsWith('/') ? withoutParams.slice(0, -1) : withoutParams;
        const parts = cleanPath.split('/');
        return parts[parts.length - 1];
      }
      // Otherwise just return the trimmed string
      return trimmed;
    }
  }, []);

  /**
   * Handle pasting a URL from the clipboard
   * @param {Event} e - The click event
   */
  const handlePasteUrl = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      const text = await navigator.clipboard.readText();
      const id = extractIdFromUrl(text);
      if (id) {
        // Update search query if modal is open
        if (isTournamentPickerOpen) {
          setSearchQuery(id);
        }

        // Check if we know the tournament type for this ID
        handleSelectTournament(id);

        // If we extracted a URL, we might want to show some feedback
        console.log(`Extracted tournament ID: ${id} from pasted content`);
      }
    } catch (err) {
      console.error('Failed to read clipboard:', err);
      // Fallback: if clipboard permission is denied, we can't do much automatically
      // but maybe show a prompt or just let them paste manually
    }
  };

  // Compute display value for the input field
  const displayValue = useMemo(() => {
    if (!value) return '';

    // First check the tournaments list
    const tournament = tournaments.find(t => t.tournamentId.toString() === value);
    if (tournament && tournament.name) {
      return `${tournament.name} - ${value}`;
    }

    // Fallback: check if the current results object matches this ID
    if (results && results.tournamentId && results.tournamentId.toString() === value && results.title) {
      return `${results.title} - ${value}`;
    }

    return value;
  }, [value, tournaments, results]);

  // Format tournament type for display
  const formatTournamentType = (type) => {
    return getTournamentTypeDisplay(type);
  };

  // Handle increment button click
  const handleIncrement = () => {
    if (!value) {
      // If no value, start with 1
      handleSelectTournament('1');
    } else {
      // Increment the current value
      const newValue = (parseInt(value, 10) + 1).toString();

      // Check if we already know the tournament type for this ID
      const newType = findTournamentTypeById(newValue);
      if (newType) {
        // Update the results context with the new type
        setTournamentIdOnly(newValue, newType);
        // If we have the tournament data already, update the context with the type
        if (onChange) {
          onChange(newValue);
        }
      } else {
        // Proceed with normal selection
      }
      handleSelectTournament(newValue);
    }
  };

  // Handle decrement button click
  const handleDecrement = () => {
    if (!value) {
      return; // Do nothing if no value
    } else {
      // Ensure we don't go below 1
      const currentValue = parseInt(value, 10);
      if (currentValue > 1) {
        const newValue = (currentValue - 1).toString();

        // Check if we already know the tournament type for this ID
        const newType = findTournamentTypeById(newValue);
        if (newType) {
          // Update the results context with the new type
          setTournamentIdOnly(newValue, newType);
          // If we have the tournament data already, update the context with the type
          if (onChange) {
            onChange(newValue);
          }
        } else {
          // Proceed with normal selection
        }
        handleSelectTournament(newValue);
      }
    }
  };

  // QR Scanner effect
  useEffect(() => {
    if (showScanner && videoRef.current) {
      const startScanner = async () => {
        try {
          await navigator.mediaDevices.getUserMedia({ video: true });
          scannerRef.current = new QrScanner(
            videoRef.current,
            (result) => {
              console.log('Tournament ID from QR code:', result.data);
              setShowScanner(false);
              if (tournamentAppConfiguration.qrScan?.processor) {
                tournamentAppConfiguration.qrScan.processor(result.data);
              }
            },
            {
              highlightScanRegion: true,
              highlightCodeOutline: true,
            }
          );
          await scannerRef.current.start();
        } catch (error) {
          console.error('Camera error:', error);
          alert('Camera access denied or not available. Please allow camera permissions.');
          setShowScanner(false);
        }
      };
      startScanner();
    }
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop();
        scannerRef.current.destroy();
      }
    };
  }, [showScanner, tournamentAppConfiguration.qrScan]);

  return (
    <>
      {showTrigger && (
        <div className={`tournament-input-wrapper ${tournamentType ? 'has-type-indicator' : ''} ${className}`}>
          <div
            className={`tournament-id-input ${theme}`}
            onClick={() => setIsTournamentPickerOpen(true)}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setIsTournamentPickerOpen(true);
              }
            }}
          >
            {displayValue || placeholder}
            {tournamentType && (
              <span className={`input-type-indicator type-${tournamentType}`}>
                {formatTournamentType(tournamentType)}
              </span>
            )}
          </div>
          {tournamentAppConfiguration.showIdIncrement && (
            <div className="tournament-id-controls">
              <button
                type="button"
                className="tournament-id-button decrement"
                onClick={handleDecrement}
                aria-label="Decrement tournament ID"
              >
                -
              </button>
              <button
                type="button"
                className="tournament-id-button increment"
                onClick={handleIncrement}
                aria-label="Increment tournament ID"
              >
                +
              </button>
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={isTournamentPickerOpen}
        onClose={() => setIsTournamentPickerOpen(false)}
        title="Select Tournament ID"
        size="large"
      >
        <div className="tournament-picker-container">
          <div className="tournament-search-box paste-container">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              placeholder="Search by tournament ID, name, or game name..."
              className={theme}
              autoFocus
              autoComplete="off"
              data-lpignore="true"
            />
            <button
              type="button"
              className={`paste-url-button ${theme}`}
              onClick={handlePasteUrl}
              title="Paste URL from clipboard"
            >
              Paste URL
            </button>
          </div>

          {tournamentAppConfiguration.qrScan && searchQuery.trim() === '' && (
            <div className="qr-scan-option">
              <div
                className="create-tournament-item"
                onClick={() => setShowScanner(true)}
              >
                <div className="create-tournament-icon">📷</div>
                <div className="create-tournament-text">
                  <div>Scan Tournament QR Code</div>
                  <div className="create-tournament-subtext">
                    Use your camera to scan a tournament QR code
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="tournament-list-container">
            {isLoading ? (
              <div className="tournament-loading">Loading tournaments...</div>
            ) : (
              <div className="tournament-list">
                {filteredTournaments.length > 0 ? (
                  filteredTournaments.map(tournament => {
                    const type = tournament.type || 'unknown';
                    const isCurrent = currentTournamentsByType[type] &&
                      currentTournamentsByType[type].tournamentId === tournament.tournamentId;

                    // Create a style object for current tournaments to use the type's color
                    const currentStyle = isCurrent ? {
                      borderColor: `var(--${type}-color, var(--primary-color))`,
                      boxShadow: `0 2px 4px var(--${type}-shadow-color, rgba(76, 175, 80, 0.2))`
                    } : {};

                    return (
                      <div
                        key={tournament.tournamentId}
                        className={`tournament-item ${tournament.tournamentId.toString() === value ? 'selected' : ''} ${isCurrent ? 'current' : ''}`}
                        style={currentStyle}
                        onClick={() => handleSelectTournament(tournament.tournamentId.toString())}
                      >
                        <div className="tournament-item-header">
                          <div className="tournament-title">{tournament.name || 'Untitled Tournament'}</div>
                          <div className="tournament-type-container">
                            {isCurrent && (
                              <span className={`active-badge type-${type}`}>
                                In Progress
                              </span>
                            )}
                            {tournament.type && (
                              <span className={`tournament-type type-${tournament.type}`}>
                                {formatTournamentType(tournament.type)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="tournament-item-details">
                          <span className="tournament-id">ID: {tournament.tournamentId}</span>
                          {(tournament.start || tournament.end) && (
                            <span className="tournament-date">
                              {tournament.start && new Date(tournament.start).toLocaleDateString()}
                              {tournament.start && tournament.end && ' - '}
                              {tournament.end && new Date(tournament.end).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        {tournament.games && tournament.games.length > 0 ? (
                          <div className="tournament-games-container">
                            {tournament.games.map((game, index) => (
                              <span key={index} className="tournament-game-pill">
                                {game}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div className="tournament-games-container">
                            <span className="tournament-game-pill no-games">No games</span>
                          </div>
                        )}
                        {tournament.date && (
                          <div className="tournament-date">{new Date(tournament.date).toLocaleDateString()}</div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="no-tournaments">
                    {searchQuery.trim() !== '' ? (
                      <div
                        className="create-tournament-item"
                        onClick={() => handleDirectInput(searchQuery.trim())}
                      >
                        <div className="create-tournament-icon">🔍</div>
                        <div className="create-tournament-text">
                          <div>Use tournament ID: {searchQuery.trim()}</div>
                          <div className="create-tournament-subtext">
                            This tournament ID is not in the list but you can still try it
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>No tournaments found matching your search</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {showScanner && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1001
          }}>
            <div style={{
              backgroundColor: theme === 'dark' ? '#333' : 'white',
              color: theme === 'dark' ? '#f5f5f5' : '#333',
              padding: '20px',
              borderRadius: '8px',
              position: 'relative'
            }}>
              <button
                onClick={() => setShowScanner(false)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: 'inherit'
                }}
              >
                ×
              </button>
              <h3>Scan Tournament QR Code</h3>
              <video ref={videoRef} style={{ width: '300px', height: '300px' }} />
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

export default TournamentIdPicker;